"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import logo from "../../assets/DHMS_logo.webp";
import loginImage from "../../assets/Login1.webp";
import AuthService from "../../Services/authService";
import GoogleAuthService from "../../Services/googleAuthService";
import Toast from "../../Components/Modals/Toast/Toast";
import ValidationModal from "../../Components/Modals/Validation/Validation";

const EyeIcon = ({ open }) =>
  open ? (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ) : (
    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );

const LoginForm = () => {
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "", remember: false });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ show: false, message: "", type: "info" });

  const showToast = (message, type = "info") => setToast({ show: true, message, type });
  const hideToast = () => setToast({ show: false, message: "", type: "info" });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const validateForm = () => {
    const e = {};
    if (!formData.email.trim()) {
      e.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      e.email = "Please enter a valid email address";
    }
    if (!formData.password) e.password = "Password is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      await AuthService.userLogin({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });
      showToast("Credentials verified. Please complete identity verification.", "info");
      setShowValidationModal(true);
    } catch (error) {
      const msg = (error.message || "").toLowerCase();
      if (msg.includes("not found") || msg.includes("does not exist") || msg.includes("no account")) {
        setErrors((p) => ({ ...p, email: "Email address does not exist." }));
      } else if (
        msg.includes("password") || msg.includes("401") ||
        msg.includes("invalid") || msg.includes("incorrect") || msg.includes("wrong")
      ) {
        setErrors((p) => ({ ...p, password: "You've entered a wrong password" }));
      } else if (msg.includes("network")) {
        showToast("Network error. Please check your internet connection.", "error");
      } else {
        showToast(error.message || "Login failed. Please try again.", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidateCode = async (validationData) => {
    setIsValidating(true);
    try {
      await AuthService.validateUserWithCode(validationData);
      const storedToken = AuthService.getAccessToken();
      if (!storedToken) {
        showToast("Verification incomplete. Please try logging in again.", "error");
        return;
      }
      showToast("Identity verified successfully! Welcome back to DHMS.", "success");
      setShowValidationModal(false);
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (error) {
      showToast(error.message || "Code verification failed. Please try again.", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleValidateId = async (validationData) => {
    setIsValidating(true);
    try {
      await AuthService.validateUserWithId(validationData);
      const storedToken = AuthService.getAccessToken();
      if (!storedToken) {
        showToast("Verification incomplete. Please try logging in again.", "error");
        return;
      }
      showToast("Identity verified successfully! Welcome back to DHMS.", "success");
      setShowValidationModal(false);
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (error) {
      showToast(error.message || "Invalid unique ID. Please try again.", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleResendCode = async (email) => {
    try {
      await AuthService.requestVerificationCode(email);
      showToast("Verification code has been resent to your email.", "success");
    } catch (error) {
      showToast(error.message || "Failed to resend code. Please try again.", "error");
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      const result = await GoogleAuthService.signIn();
      const user = result.userInfo || {};
      if (!user.email) {
        showToast("Could not retrieve email from Google. Please try again.", "error");
        return;
      }
      await AuthService.googleSignin({
        email: user.email,
        name: user.fullName || user.name || user.email,
      });
      const storedToken = AuthService.getAccessToken();
      if (!storedToken) {
        showToast("No account found for this Google email. Please sign up first.", "error");
        return;
      }
      showToast("Logged in with Google successfully!", "success");
      setTimeout(() => navigate("/dashboard"), 800);
    } catch (error) {
      const msg = (error.message || "").toLowerCase();
      let message = "Google login failed. Please try again.";
      if (msg.includes("popup_closed_by_user")) message = "Sign-in was cancelled. Please try again.";
      else if (msg.includes("network")) message = "Network error. Please check your internet connection.";
      else if (msg.includes("404") || msg.includes("not found")) message = "No account found for this Google email. Please sign up first.";
      else if (error.message) message = error.message;
      showToast(message, "error");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleCloseValidationModal = () => {
    setShowValidationModal(false);
    showToast("Verification cancelled. You can complete it later from your profile.", "info");
  };

  return (
    <>
      <div className="L-container">

        {/* Logo — absolute top-left over image */}
        <a href="/" className="L-logo">
          <img src={logo} alt="IT Service Desk Africa" className="L-logo-img" />
        </a>

        {/* Left: hero image */}
        <div className="L-left">
          <img src={loginImage} alt="Family using devices" className="L-hero-img" />
        </div>

        {/* Right: form panel */}
        <div className="L-right">
          <div className="L-card">

            {/* Welcome */}
            <div className="L-welcome">
              <h1 className="L-title">👋 Welcome back!!!</h1>
              <p className="L-subtitle">Log in to manage your family's devices</p>
            </div>

            <form onSubmit={handleSubmit} noValidate>

              {/* Email */}
              <div className="L-form-group">
                <label htmlFor="L-email">Email</label>
                <input
                  type="email"
                  id="L-email"
                  name="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? "L-error" : ""}
                  disabled={isLoading || isGoogleLoading}
                />
                {errors.email && <span className="L-error-msg">{errors.email}</span>}
              </div>

              {/* Password */}
              <div className="L-form-group">
                <label htmlFor="L-password">Password</label>
                <div className="L-pw-wrap">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    id="L-password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    className={errors.password ? "L-error" : ""}
                    disabled={isLoading || isGoogleLoading}
                  />
                  <button
                    type="button"
                    className="L-pw-toggle"
                    onClick={() => setPasswordVisible((v) => !v)}
                    tabIndex={-1}
                    disabled={isLoading || isGoogleLoading}
                  >
                    <EyeIcon open={passwordVisible} />
                  </button>
                </div>
                {errors.password && <span className="L-error-msg">{errors.password}</span>}
              </div>

              {/* Remember me */}
              <div className="L-remember">
                <label className="L-checkbox-label">
                  <input
                    type="checkbox"
                    name="remember"
                    checked={formData.remember}
                    onChange={handleChange}
                    disabled={isLoading || isGoogleLoading}
                    className="L-checkbox"
                  />
                  Remember me
                </label>
              </div>

              {/* Log in button */}
              <button type="submit" className="L-btn" disabled={isLoading || isGoogleLoading}>
                {isLoading ? "Logging in…" : "Log in"}
              </button>

              {/* Forgot Password */}
              <div className="L-forgot">
                <a href="/forgotPassword">Forgot Password?</a>
              </div>

            </form>

            {/* Divider */}
            <div className="L-divider">or</div>

            {/* Google button */}
            <button
              type="button"
              className="L-google-btn"
              onClick={handleGoogleLogin}
              disabled={isLoading || isGoogleLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" className="L-google-icon">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {isGoogleLoading ? "Processing…" : "Login  with google"}
            </button>

            {/* Sign up link */}
            <p className="L-signup-link">
              Don't&nbsp; have an account? <a href="/signup">Sign up</a>
            </p>

          </div>
        </div>
      </div>

      <ValidationModal
        isOpen={showValidationModal}
        onClose={handleCloseValidationModal}
        onValidateCode={handleValidateCode}
        onValidateId={handleValidateId}
        onResendCode={handleResendCode}
        userEmail={formData.email}
        isLoading={isValidating}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.show}
        onClose={hideToast}
        duration={5000}
      />
    </>
  );
};

export default LoginForm;
